import { MsalAuthenticationTemplate, MsalProvider, useMsal } from '@azure/msal-react';
import type { IPublicClientApplication} from '@azure/msal-browser';
import { InteractionType } from '@azure/msal-browser';
import type { Theme, TableRowId } from '@fluentui/react-components';
import { FluentProvider, Link, Toast, ToastBody, ToastFooter, ToastTitle, ToastTrigger, Toaster, makeStyles, useToastController, webDarkTheme, webLightTheme, useId, Spinner } from '@fluentui/react-components';
import { loginRequest } from './authConfig';
import { useCallback, useEffect, useState } from 'react';
import { PageLayout } from './components/PageLayout/PageLayout';
import { ConfigurationDataToolbar } from './components/ConfigurationDataGrid/ConfigurationDataToolbar/ConfigurationDataToolbar';
import { LinksDrawer } from './components/LinksDrawer/LinksDrawer';
import { customApiError, customOtherError, subscribeToErrors, unSubscribeFromErrors } from './events';
import type { IConfigurationInfo} from './interfaces';
import { EThemeName } from './interfaces';
import { ConfigurationDataGrid } from './components/ConfigurationDataGrid/ConfigurationDataGrid';
import { fetchConfigurationsForTenantAsync } from './apiRequests/settings/settings';
import { ConfigurationDataDrawer } from './components/ConfigurationDataDrawer/ConfigurationDataDrawer';
import { getSettingsDataFromSelectedItem } from './utilities/SettingsHelper/SettingsHelper';
import { CreateConfigurationDrawer } from './components/CreateConfigurationDrawer/CreateConfigurationDrawer';

export interface IAppProps {
  msalInstance: IPublicClientApplication;
}

const useStyles = makeStyles({
  spinner: {
    position: "absolute",
    top: "50%",
    left: "50%"
  }
});

export function MainContent() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const styles = useStyles();

  const [settingsData, setSettingsData] = useState<IConfigurationInfo[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<TableRowId>>(new Set<TableRowId>);
  const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false);
  const [selectedSettings, setSelectedSettings] = useState<IConfigurationInfo>();
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateSettingsDialog, setShowCreateSettingsDialog] = useState(false);

  const selectedItemsChanged = (newSelectedItems: Set<TableRowId>) => {
    setSelectedItems(newSelectedItems);
    setIsSettingsDrawerOpen(true);

    const newSelectedSettings = getSettingsDataFromSelectedItem(newSelectedItems, settingsData);
    setSelectedSettings(newSelectedSettings);
  };

  const onSettingsDrawerOpenChanged = async (isOpen: boolean, refresh: boolean): Promise<void> => {
    setIsSettingsDrawerOpen(isOpen);

    if (refresh) {
      await onRefreshConfigurations();
    }
  };

  const onCreateSettingsDialogOnOpenChanged = async (isOpen: boolean): Promise<void> => {
    setShowCreateSettingsDialog(isOpen);

    if (!isOpen) {
      await onRefreshConfigurations();
    }
  };

  const createSettingsClicked = (): void => {
    setShowCreateSettingsDialog(true);
  };

  const refreshSettingsData = useCallback(async () => {
    const data = await fetchConfigurationsForTenantAsync(instance, account);

    if (Array.isArray(data) && data.length > 0) {
      setSettingsData(data);
    } else {
      setSettingsData([]);
    }
  }, [instance, account]);

  const onRefreshConfigurations = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    await refreshSettingsData();
    setIsLoading(false);
  }, [refreshSettingsData]);

  useEffect(() => {
    void onRefreshConfigurations();
  }, [onRefreshConfigurations]);


  return (
    <div className="App">
      {selectedSettings && <ConfigurationDataDrawer
        configurationInfo={selectedSettings}
        open={isSettingsDrawerOpen}
        onOpenChange={onSettingsDrawerOpenChanged} />}
      <div>
        <h2>Configurations</h2>
        <div>Select an application configuration to manage it.</div>
        <ConfigurationDataToolbar onAddSettings={() => createSettingsClicked()} onRefresh={() => onRefreshConfigurations()} />
        {isLoading && <Spinner labelPosition="below" label="Loading..." className={styles.spinner} />}
        {!isLoading && settingsData
          ? <ConfigurationDataGrid settingsItems={settingsData} selectedItems={selectedItems} onSelectionChange={selectedItemsChanged} />
          : <></>
        }
        {showCreateSettingsDialog && <CreateConfigurationDrawer
          onOpenChange={onCreateSettingsDialogOnOpenChanged} />}
      </div>
    </div>
  );
};


function App(props: Readonly<IAppProps>) {
  const { msalInstance } = props;
  const [isAppDrawerOpen, setIsAppDrawerOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<Theme>(webLightTheme);

  const toasterId = useId("toaster");
  const toastId = useId("toast");
  const { dispatchToast } = useToastController(toasterId);

  const setTheme = (themeName: EThemeName) => {
    switch (themeName) {
      case EThemeName.LightTheme:
        setCurrentTheme(webLightTheme);
        document.body.style.backgroundColor = webLightTheme.colorNeutralBackground1;
        return;

      case EThemeName.DarkTheme:
        setCurrentTheme(webDarkTheme);
        document.body.style.backgroundColor = webDarkTheme.colorNeutralBackground1;
        return;
    }
  };

  const darkModeMatchQuery = '(prefers-color-scheme: dark)';
  if (globalThis.matchMedia?.(darkModeMatchQuery)?.matches) {
    if (currentTheme !== webDarkTheme) {
      setTheme(EThemeName.DarkTheme);
    }
  }

  const notifyError = useCallback((event: Event) => {
    switch (event.type) {
      case customApiError:
        dispatchToast(
          <Toast>
            <ToastTitle action={
              <ToastTrigger>
                <Link>Dismiss</Link>
              </ToastTrigger>}>Connection problem</ToastTitle>
            <ToastBody>Unable to connect to the MacroView settings service right now. Please try again later. </ToastBody>
            <ToastFooter>
              <Link href="https://support.macroview.com.au/hc/en-us/requests/new" target='_blank'> Contact MacroView Support</Link>
            </ToastFooter>
          </Toast>,
          { toastId: toastId, intent: "warning", timeout: 600000 }
        );
        break;

      case customOtherError:
        dispatchToast(
          <Toast>
            <ToastTitle action={
              <ToastTrigger>
                <Link>Dismiss</Link>
              </ToastTrigger>}>An error has occurred</ToastTitle>
            <ToastBody>{(event as CustomEvent).detail}</ToastBody>
            <ToastFooter>
              <Link href="https://support.macroview.com.au/hc/en-us/requests/new" target='_blank'> Contact MacroView Support</Link>
            </ToastFooter>
          </Toast>,
          { toastId: toastId, intent: "warning", timeout: 600000 }
        );
        break;

      default:
        break;
    }
  }, [dispatchToast, toastId]);

  useEffect(() => {
    function colorSchemeChangeListener(this: MediaQueryList, event: MediaQueryListEvent) {
      if (event.matches) {
        setTheme(EThemeName.DarkTheme);
      } else {
        setTheme(EThemeName.LightTheme);
      }
    }

    globalThis.matchMedia?.(darkModeMatchQuery)?.addEventListener('change', colorSchemeChangeListener);
    subscribeToErrors(notifyError);

    return () => {
      globalThis.matchMedia?.(darkModeMatchQuery)?.removeEventListener('change', colorSchemeChangeListener);
      unSubscribeFromErrors(notifyError);
    };

    // ensure event handlers are registered only once
  }, [notifyError]);

  const onAppDrawerOpenChanged = (isOpen: boolean): void => {
    setIsAppDrawerOpen(isOpen);
  };

  const onNavigationBarWaffleClicked = (): void => {
    setIsAppDrawerOpen(!isAppDrawerOpen);
  };

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
        <FluentProvider theme={currentTheme}>
          <PageLayout onWaffleClick={onNavigationBarWaffleClicked}>
            <MainContent />
            <LinksDrawer open={isAppDrawerOpen} onOpenChange={onAppDrawerOpenChanged} />
            <Toaster toasterId={toasterId} limit={3} />
          </PageLayout>
        </FluentProvider>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

export default App;
